<template>
  <div class="mb-4 flex justify-end py-2">
    <DateFilter
      :model-value="globalQuery"
      :time-period-label="timePeriodLabel"
      range-type="custom"
      @update:model-value="setGlobalQuery"
    />
  </div>

  <BaseCard class="mb-4">
    <div class="flex grow items-center space-x-2">
      <div
        class="grid w-full grid-cols-2 gap-2 md:grid-cols-4"
      >
        <ReportCashierFilter v-model="filters.cashierId" />
        <ReportDeviceFilter v-model="filters.deviceId" />

        <div />
        <div class="flex justify-end">
          <ButtonExport
            :is-loading="isExporting"
            :default-file-export-format="
              tenant.defaultExportFiletype
            "
            @export="(format) => onExport(format)"
          />
        </div>
      </div>
    </div>
  </BaseCard>

  <BaseCard class="relative mb-4">
    <QueryLoading v-if="isLoading" class="min-h-96" />
    <QueryError
      v-else-if="error"
      class="min-h-96"
      :show-retry="true"
      @retry="refetch"
    >
      Failed to load Sales Overview

      <template v-if="isErrorDatabaseTimeout">
        <br />
        <span class="font-normal">
          The request took too long to process, try a
          smaller date range.
        </span>
      </template>
    </QueryError>

    <dl
      v-else
      class="grid auto-rows-fr grid-cols-3 gap-4 p-4"
    >
      <template
        v-for="line in formattedData"
        :key="line.label"
      >
        <dt class="col-span-2 flex flex-col justify-center">
          <p class="text-lg">{{ line.label }}</p>
          <p
            v-if="line.info"
            class="flex items-center text-sm"
          >
            <IconInfo class="mr-1 text-info" />
            {{ line.info }}
          </p>
        </dt>
        <dd class="flex items-center justify-end">
          <div class="text-lg font-semibold text-primary">
            {{ formatToMerchantCurrencyString(line.value) }}
          </div>
        </dd>
      </template>
    </dl>

    <BaseWarningBox
      v-if="data?.warnIncorrectCurrencyTransaction"
      class="mt-4"
    >
      <p>
        Due to the existence of transactions not in
        {{ tenant.currency }}, some transactions will not be
        accounted for in the "Payments Received" and "Amount
        Refunded" totals above.
      </p>
    </BaseWarningBox>
  </BaseCard>
</template>

<script setup lang="ts">
import type {
  OverviewReportQuery,
  OverviewReportFilters,
} from "@/services/reports/retrieveOverviewReport"
import type { FileExportFormat } from "@/composables/options/useFileExportFormatOptions"
import { ApiError } from "@/api"

const { formatToMerchantCurrencyString } = useCurrency()

const { tenant } = useTenant()

const filters = ref<OverviewReportFilters>({
  cashierId: undefined,
  deviceId: undefined,
})

const { globalQuery, setGlobalQuery, timePeriodLabel } =
  useReportQuery<OverviewReportQuery>({})

const viewingQuery = computed(() => ({
  ...globalQuery.value,
  ...filters.value,
}))
const { data, isLoading, error, refetch } =
  useOverviewReport(viewingQuery)

const isErrorDatabaseTimeout = computed(
  () =>
    error.value instanceof ApiError &&
    error.value.code === "database-timeout"
)

const formattedData = computed(() =>
  data.value
    ? [
        {
          label: "Item Sales",
          info: tenant.isVatEnabled
            ? "VAT inclusive total of all products sold"
            : "Total of all products sold",
          value: data.value.totalItemGross,
        },
        ...(tenant.isServiceChargeEnabled ||
        data.value.totalServiceCharge > 0
          ? [
              {
                label: "Service Charge",
                info: "Total service charge applied on orders",
                value: data.value.totalServiceCharge,
              },
            ]
          : []),
        {
          label: "Payments Received",
          info: "Total payments received, not including gratuity",
          value: data.value.totalGrossReceived,
        },
        {
          label: "Amount Refunded",
          info: "Amount refunded against non-gratuity payments",
          value: data.value.totalGrossRefunded,
        },
        ...(tenant.isGratuityEnabled ||
        data.value.totalGratuityReceived > 0 ||
        data.value.totalGratuityRefunded > 0
          ? [
              {
                label: "Gratuity Received",
                info: "Gratuity received, not including service charge",
                value: data.value.totalGratuityReceived,
              },
              {
                label: "Gratuity Refunded",
                info: "Total amount refunded against gratuity payments",
                value: data.value.totalGratuityRefunded,
              },
            ]
          : []),
      ]
    : undefined
)

const isExporting = ref(false)
const onExport = async (format?: FileExportFormat) => {
  if (isExporting.value) return
  isExporting.value = true
  try {
    await exportSales(viewingQuery.value, format)
  } catch (error) {
    if (error instanceof ApiError) {
      error.code === "database-timeout"
        ? toast.error(
            "The request took too long to process. Try a smaller date range.",
            4000
          )
        : toast.error("Failed to export sales", 3000)
    } else {
      toast.error("Failed to export sales", 3000)
    }
  } finally {
    isExporting.value = false
  }
}
</script>
